import React from "react";
import { Schema } from "@sparefoot/react-commons";

/**
 * Combines multiple schema objects into a single JSON-LD graph structure
 * @param {Array<Object>} schemas - Array of schema objects to combine
 * @returns {Object} Combined schema with @graph structure
 */
const combineSchemaObjects = (schemas) => {
	if (!schemas || !Array.isArray(schemas) || schemas.length === 0) {
		return null;
	}

	const validSchemas = schemas.filter((schema) => schema != null);

	if (validSchemas.length === 0) {
		return null;
	}

	if (validSchemas.length === 1) {
		const singleSchema = validSchemas[0];
		if (singleSchema["@graph"]) {
			// If already has @graph, ensure items don't have redundant @context
			const cleanedGraphItems = singleSchema["@graph"].map((item) => {
				const { "@context": itemContext, ...itemWithoutContext } = item;
				return itemWithoutContext;
			});
			return {
				"@context": singleSchema["@context"] || "https://schema.org",
				"@graph": cleanedGraphItems
			};
		}
		// Remove @context from single schema when wrapping in @graph
		const { "@context": schemaContext, ...schemaWithoutContext } =
			singleSchema;
		return {
			"@context": schemaContext || "https://schema.org",
			"@graph": [schemaWithoutContext]
		};
	}

	const graphItems = [];
	let context = "https://schema.org";

	validSchemas.forEach((schema) => {
		if (schema["@graph"]) {
			// If schema already has @graph, extract and add its items
			const items = schema["@graph"].map((item) => {
				// Remove @context from nested items as it will be at the top level
				const { "@context": itemContext, ...itemWithoutContext } = item;
				return itemWithoutContext;
			});
			graphItems.push(...items);
			if (schema["@context"]) {
				context = schema["@context"];
			}
		} else {
			// Remove @context from individual schema as it will be at the top level
			const { "@context": schemaContext, ...schemaWithoutContext } =
				schema;
			graphItems.push(schemaWithoutContext);
			if (schemaContext && context === "https://schema.org") {
				context = schemaContext;
			}
		}
	});

	return {
		"@context": context,
		"@graph": graphItems
	};
};

/**
 * Combines multiple schemas and returns a Schema component with the combined data
 * @param {Array<Object>} schemas - Array of schema objects to combine
 * @returns {React.Component|null} Schema component with combined schemas or null
 */
export const combineSchemas = (schemas) => {
	const combinedSchema = combineSchemaObjects(schemas);

	if (!combinedSchema) {
		return null;
	}

	return <Schema schema={combinedSchema} />;
};
